import React, { useState, useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { Trans, useLingui } from '@lingui/react/macro'

import { YStack, XStack } from 'tamagui'
import { Text, Paragraph } from '@newComponents/Text'
import { Button } from '@newComponents/Button/Button'
import { useMutation } from '@data/useMutation'

import { EventHostInvitationBanner_event$key } from './__generated__/EventHostInvitationBanner_event.graphql'
import { EventHostInvitationBanner_query$key } from './__generated__/EventHostInvitationBanner_query.graphql'
import { EventHostInvitationBannerAcceptMutation } from './__generated__/EventHostInvitationBannerAcceptMutation.graphql'
import { EventHostInvitationBannerDeclineMutation } from './__generated__/EventHostInvitationBannerDeclineMutation.graphql'

export const EventHostInvitationBanner = ({
  event,
  query,
}: {
  event: EventHostInvitationBanner_event$key
  query: EventHostInvitationBanner_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventHostInvitationBanner_event on Event {
        eventHosts(filter: { status: { equalTo: PENDING } }) {
          edges {
            node {
              id
              rowId
              status
              hostType
              host {
                __typename
                ... on Guild {
                  name
                  slugId
                  myMembership {
                    membershipLevel
                  }
                }
                ... on User {
                  rowId
                }
              }
            }
          }
        }
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventHostInvitationBanner_query on Query {
        viewer {
          rowId
        }
      }
    `,
    query
  )

  const [acceptError, setAcceptError] = useState<string | null>(null)
  const [declineError, setDeclineError] = useState<string | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)

  const acceptMutation = useMutation<EventHostInvitationBannerAcceptMutation>(
    graphql`
      mutation EventHostInvitationBannerAcceptMutation(
        $input: AcceptEventHostInvitationInput!
      ) {
        acceptEventHostInvitation(input: $input) {
          eventHost {
            id
            status
          }
          event {
            ...EventScreenOwner_event
          }
        }
      }
    `
  )

  const declineMutation = useMutation<EventHostInvitationBannerDeclineMutation>(
    graphql`
      mutation EventHostInvitationBannerDeclineMutation(
        $input: DeclineEventHostInvitationInput!
      ) {
        declineEventHostInvitation(input: $input) {
          eventHost {
            id
            status
          }
        }
      }
    `
  )

  const viewerRowId = queryData.viewer?.rowId
  const edges = data.eventHosts?.edges ?? []

  // Client-side filter: find the first pending invitation that belongs to the viewer
  const matchedInvitation = edges
    .map((edge) => edge?.node)
    .filter(Boolean)
    .find((node) => {
      if (!node) return false
      // Client-side status check: Relay doesn't re-evaluate server filters after
      // mutations update status, so we must check here to hide the banner after accept/decline
      if (node.status !== 'PENDING') return false
      if (node.hostType === 'USERS') {
        return (
          node.host?.__typename === 'User' &&
          node.host.rowId === viewerRowId
        )
      }
      if (node.hostType === 'GUILDS') {
        return (
          node.host?.__typename === 'Guild' &&
          (node.host.myMembership?.membershipLevel === 'ORGANIZER' ||
            node.host.myMembership?.membershipLevel === 'EDITOR')
        )
      }
      return false
    })

  const { t } = useLingui()

  const handleAccept = useCallback(async () => {
    if (!matchedInvitation) return
    setAcceptError(null)
    setIsAccepting(true)
    try {
      await acceptMutation({
        variables: {
          input: {
            pEventHostId: matchedInvitation.rowId,
          },
        },
      })
    } catch (err) {
      setAcceptError(
        err instanceof Error ? err.message : t`Something went wrong. Please try again.`
      )
    } finally {
      setIsAccepting(false)
    }
  }, [acceptMutation, matchedInvitation?.rowId])

  const handleDecline = useCallback(async () => {
    if (!matchedInvitation) return
    setDeclineError(null)
    setIsDeclining(true)
    try {
      await declineMutation({
        variables: {
          input: {
            pEventHostId: matchedInvitation.rowId,
          },
        },
      })
    } catch (err) {
      setDeclineError(
        err instanceof Error ? err.message : t`Something went wrong. Please try again.`
      )
    } finally {
      setIsDeclining(false)
    }
  }, [declineMutation, matchedInvitation?.rowId])

  if (!matchedInvitation) {
    return null
  }

  const guildName = matchedInvitation.host?.__typename === 'Guild' ? matchedInvitation.host.name : null
  const invitationText = guildName
    ? t`${guildName} has been invited to co-host this Event`
    : t`You've been invited to co-host this Event`

  return (
    <YStack
      backgroundColor='$backgroundAccent'
      padding='$5'
      borderRadius='$4'
      gap='$4'
    >
      <Paragraph size='$5'>{invitationText}</Paragraph>

      <XStack gap='$3'>
        <Button
          onPress={handleAccept}
          forceState={isAccepting ? 'pressed' : 'notPressed'}
        >
          <Button.Text><Trans>Accept</Trans></Button.Text>
        </Button>

        <Button
          secondary
          onPress={handleDecline}
          forceState={isDeclining ? 'pressed' : 'notPressed'}
        >
          <Button.Text><Trans>Decline</Trans></Button.Text>
        </Button>
      </XStack>

      {!!acceptError && (
        <Text color='$red10' size='$4'>
          {acceptError}
        </Text>
      )}

      {!!declineError && (
        <Text color='$red10' size='$4'>
          {declineError}
        </Text>
      )}
    </YStack>
  )
}
